import React from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { EditorView } from '@codemirror/view'
import { python } from '@codemirror/lang-python'
import { Avatar, Tooltip } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { Play, Minus, Check, X as XIcon } from 'lucide-react'
import '../../../css/vizmental-ide.scss'
import '../../../css/chat.scss'
import '../../../css/studentview.scss'
import { studentTestView, testsInStudentOrder } from '../testVisibility'
import { StudentTestCell } from '../TestCaseIO'

// Read-only, context-free replicas of VizmentalIDE's panes (VizmentalIDE.jsx render tree)
// for the wizard preview and the student-layout Task and Unit Test tab. They reproduce the
// real markup and reuse vizmental-ide.scss / chat.scss so they look identical, but never
// mount the real student components: VizmentalIDE runs consent checks, rrweb and
// submission writes, and AIChat writes a welcome message to the database on mount.

// The strings students actually see, copied from VizmentalIDE.jsx / AIChat.jsx.
export const VIZMENTAL_DEFAULT_TASK = 'Complete the programming task.'
export const AI_WELCOME_MESSAGE = 'Hey I am your AI assistant, feel free to ask me questions if you are stuck!'
const AI_CHARACTER_LIMIT = 500

export const READ_ONLY_RUN_TOOLTIP = 'Students run their code here — read-only in this view'

// Same look as VizmentalIDE's CodeMirror theme.
const vizmentalEditorTheme = EditorView.theme({
  '&': { height: '100%', fontSize: '14px', color: '#1f2937' },
  '.cm-content': { padding: '16px', minHeight: '100%', color: '#1f2937' },
  '.cm-focused': { outline: 'none' },
  '.cm-editor': { height: '100%', backgroundColor: '#ffffff' },
  '.cm-scroller': { height: '100%' },
  '.cm-line': { color: '#1f2937' },
})

// `sectionProps` (optional) is spread onto the section - the wizard's live preview uses it
// to mark the section as a highlightable region (live/previewRegions.js).
export const VizmentalTaskSection = ({ task, actions = null, sectionProps = null }) => (
  <div {...sectionProps} className="content-section">
    <h3 className="section-title sv-section-title-row">
      <span>Task Description</span>
      {actions}
    </h3>
    <div className="description-box">{task || VIZMENTAL_DEFAULT_TASK}</div>
  </div>
)

// The student's test table. With no rows yet (e.g. in the wizard before tests exist) it
// shows muted placeholder rows so the instructor can see where tests will appear -
// VizmentalIDE itself hides the section until the session has at least one test.
//
// `results` (optional) is the per-test pass/fail list a run produced (the harness's
// `result`, as VizmentalIDE reads it); each row then shows VizmentalIDE's PassIcon for its
// entry. Without it every row is pending, as before any run. `sectionProps` is spread onto
// the section (see VizmentalTaskSection).
export const VizmentalTestSection = ({
  testList = [],
  placeholderCount = 3,
  actions = null,
  results = null,
  sectionProps = null,
}) => {
  // Public tests first, as VizmentalIDE lists them; `index` is the row's place in
  // test_list, which is what `results` is indexed by.
  const rows = testList.length
    ? testsInStudentOrder(testList).map(({ index }) => ({
        ...studentTestView(testList, index, `Test ${index + 1}`),
        index,
        placeholder: false,
      }))
    : Array.from({ length: placeholderCount }, (_, index) => ({
        name: `Test ${index + 1}`,
        index,
        placeholder: true,
      }))
  return (
    <div {...sectionProps} className="content-section">
      <h3 className="section-title sv-section-title-row">
        <span>Test Cases</span>
        {actions}
      </h3>
      <div className="test-table-wrapper">
        <table className="test-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="result-column">Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.index} className={row.placeholder ? 'sv-placeholder-row' : undefined}>
                <td>
                  {/* As VizmentalIDE: public tests also show input and expected output. */}
                  {row.placeholder ? row.name : <StudentTestCell view={row} />}
                </td>
                <td className="result-column">
                  <VizmentalPassIcon testResult={row.placeholder || !results ? null : results[row.index]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!testList.length && (
        <p className="sv-caption">Placeholder rows — the real test names appear here once tests exist.</p>
      )}
    </div>
  )
}

// VizmentalIDE's PassIcon: pending (no result yet), passed, or failed.
export const VizmentalPassIcon = ({ testResult }) => {
  if (testResult == null) {
    return (
      <div className="test-icon test-icon-pending">
        <Minus className="w-5 h-5" />
      </div>
    )
  }
  return testResult ? (
    <div className="test-icon test-icon-passed">
      <Check className="w-5 h-5" />
    </div>
  ) : (
    <div className="test-icon test-icon-failed">
      <XIcon className="w-5 h-5" />
    </div>
  )
}

export const VizmentalCodeEditor = ({ code = '' }) => (
  <div className="code-editor">
    <CodeMirror
      value={code}
      height="100%"
      style={{ height: '100%' }}
      editable={false}
      readOnly
      extensions={[python(), vizmentalEditorTheme]}
    />
  </div>
)

// The terminal's Run button, disabled. The tooltip hangs off a wrapping span because MUI
// tooltips do not fire on disabled buttons.
export const VizmentalRunControls = ({ label = 'Run Tests', showTooltip = false }) => {
  const controls = (
    <span className="sv-run-controls">
      <button className="run-button" disabled type="button">
        <Play className="w-3 h-3" />
        {label}
      </button>
    </span>
  )
  return showTooltip ? (
    <Tooltip title={READ_ONLY_RUN_TOOLTIP} placement="top">
      {controls}
    </Tooltip>
  ) : controls
}

export const vizmentalTerminalText = (label = 'Run Tests') => `Click '${label}' to execute your code...`

// The AIChat pane as a consenting student first sees it: the welcome message and an empty
// input. Markup follows AIChat.jsx + Message.jsx (chat.scss classes).
export const VizmentalAiChat = ({ footer = null }) => (
  <div className="chat-container sv-chat" style={{ height: '100%' }}>
    <div className="chat" style={{ height: '100%' }}>
      <div className="chat-history">
        <div className="messages">
          <div className="message" style={{ justifyContent: 'left' }}>
            <div className="messageInfo">
              <Avatar sx={{ bgcolor: '#1976d2', width: 26, height: 26 }}>
                <SmartToyIcon sx={{ fontSize: '1rem' }} />
              </Avatar>
              <span className="info-user">AI assistant</span>
            </div>
            <div className="messageContentWrapper">
              <div className="messageContent">
                <pre>{AI_WELCOME_MESSAGE}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
      {footer}
      <form className="message-form" style={{ position: 'relative' }} onSubmit={(e) => e.preventDefault()}>
        <textarea
          className="message-input"
          placeholder="Use Shift + Enter for new line"
          readOnly
          disabled
          style={{ paddingBottom: '28px' }}
        />
        <div className="sv-chat-counter">0/{AI_CHARACTER_LIMIT}</div>
        <button
          type="button"
          className="send-button"
          disabled
          style={{ backgroundColor: '#efefef', cursor: 'not-allowed' }}>
          Send
        </button>
      </form>
    </div>
  </div>
)
